import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
);

const useStyles = makeStyles((_theme) => ({
    chartContainer: {
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '20vw',
        minHeight: '300px'
    },
    sliderContainer: {
        margin: '20px 0',
        padding: '0 30px',
        position: 'relative',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    sliderWrapper: {
        position: 'relative',
        height: '8px',
        display: 'flex',
        alignItems: 'center',
    },
    slider: {
        WebkitAppearance: 'none',
        width: '100%',
        height: '4px',
        background: '#e0e0e0',
        borderRadius: '2px',
        outline: 'none',
        '&::-webkit-slider-thumb': {
            WebkitAppearance: 'none',
            width: '20px',
            height: '20px',
            background: '#0067A1',
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:hover': { transform: 'scale(1.1)', background: '#004d80' },
            '&:active': { transform: 'scale(1)' }
        },
    },
    pageIndicator: {
        position: 'absolute',
        right: '0',
        bottom: '-24px',
        color: '#666',
        fontSize: '0.8rem',
        fontWeight: 400,
        fontFamily: 'system-ui, sans-serif',
        '& span': { color: '#0067A1', fontWeight: 500 }
    },
    progressTrack: {
        position: 'absolute',
        height: '4px',
        background: '#0067A1',
        borderRadius: '2px',
        transition: 'width 0.3s ease',
    },
}));

const GraphWithMinandMax = ({ graphData = [], activeTab }) => {
    const classes = useStyles();
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 5;
    const totalPages = Math.ceil(graphData.length / itemsPerPage);
    const paginatedData = graphData.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    const getMaxValue = () => {
        const allValues = graphData.flatMap(item => [
            item.Forecast,
            item.Change,
            item.Awards,
            item.forecast_max
        ]);
        return Math.max(...allValues, 0) * 1.15;
    };


    const commonSeriesProperties = {
        borderWidth: 0,
        categoryPercentage: 0.6,
        barPercentage: 0.5,
        borderRadius: {
          topLeft: 10,
          topRight: 10,
          bottomLeft: 0,
          bottomRight: 0
        }
      };
      
      const commonSeries = [
        {
          label: 'Forecast',
          data: paginatedData.map(item => item.Forecast),
          backgroundColor: '#EC6FBD',
          ...commonSeriesProperties
        },
        {
          label: 'Awards',
          data: paginatedData.map(item => item.Awards),
          backgroundColor: '#A4D65D',
          ...commonSeriesProperties
        }
      ];
      
      const differenceSeries = activeTab === 'Under Capacity' 
        ? {
            label: 'Shortage',
            data: paginatedData.map(item => item.Change),
            backgroundColor: '#0067A1',
            ...commonSeriesProperties
          }
        : {
            label: 'Excess',
            data: paginatedData.map(item => item.change),
            backgroundColor: '#0067A1',
            ...commonSeriesProperties
          };
      
      const TabChange = [...commonSeries, differenceSeries];



    const chartData = {
        labels: paginatedData.map(item => item.LaneID),
        datasets: TabChange,
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                align: 'end',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle' // other options: 'rect', 'triangle', etc.
                }
            },
            tooltip: {
                callbacks: {
                    afterBody: (context) => {
                        const index = context[0].dataIndex;
                        return [
                            `Forecast Max: ${paginatedData[index].forecast_max}`,
                            `Forecast Min: ${paginatedData[index].forecast_min}`
                        ];
                    }
                }
            },
            annotation: {
                annotations: paginatedData.flatMap((item, index) => {
                    const barPosition = index - 0.2;
                    const tickWidth = 0.04;

                    return [
                        {
                            type: 'line',
                            xMin: barPosition,
                            xMax: barPosition,
                            yMin: item.forecast_min,
                            yMax: item.forecast_max,
                            borderColor: 'black',
                            borderWidth: 2,
                        },
                        {
                            type: 'line',
                            xMin: barPosition - tickWidth,
                            xMax: barPosition + tickWidth,
                            yMin: item.forecast_max,
                            yMax: item.forecast_max,
                            borderColor: 'black ',
                            borderWidth: 2,
                            width: 0.5
                        },
                        {
                            type: 'line',
                            xMin: barPosition - tickWidth,
                            xMax: barPosition + tickWidth,
                            yMin: item.forecast_min,
                            yMax: item.forecast_min,
                            borderColor: 'black',
                            borderWidth: 2,
                        }
                    ];
                })
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: getMaxValue(),
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Truck Loads',
                    font: {
                        size: 14
                    },
                    color: '#333' // Optional: customize color
                }
            },
            x: {
                grid: {
                    display: false
                },
                title: { display: false },
                ticks: {
                    autoSkip: false
                }
            }
        }
    };

    return (
        <div>
            <div className={classes.chartContainer}>
                <Bar data={chartData} options={options} />
            </div>

            {totalPages > 1 && (
                <div className={classes.sliderContainer}>
                    <div className={classes.sliderWrapper}>
                        <div
                            className={classes.progressTrack}
                            style={{
                                width: `${((currentPage + 1) / totalPages) * 100}%`,
                                opacity: 0.3
                            }}
                        />
                        <input
                            type="range"
                            min="0"
                            max={totalPages - 1}
                            value={currentPage}
                            onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
                            className={classes.slider}
                        />
                    </div>
                    <div className={classes.pageIndicator}>
                        <span>{currentPage + 1}</span> / {totalPages}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GraphWithMinandMax;